import { t } from "@lingui/macro";
import { Avatar, useTheme } from "@mui/material";
import { FitnessStrengths, FitnessType, FitnessWeakness } from "@WahooFitness/cloud-client-types";
import {
  WfIconSportRyderTypesClimber,
  WfIconSportRyderTypesMarathoner,
  WfIconSportRyderTypesMiler,
  WfIconSportRyderTypesRouleur,
  WfIconSportRyderTypesSpeedster,
  WfIconSportRyderTypesTimetrialist,
} from "@WahooFitness/redesignr";
import { useMemo } from "react";

const useLocalizedRunningFitnessData = (iconSize = 20) => {
  const { palette } = useTheme();
  const runnerTypes = useMemo(
    () => [
      {
        id: FitnessType.Speedster,
        stringId: "Speedster",
        icon: (
          <WfIconSportRyderTypesSpeedster
            sx={{ width: "100%", height: "100%", color: palette.riderType.attacker.main }}
          />
        ),
        iconFilled: (
          <Avatar
            sx={{
              bgcolor: palette.riderType.attacker.semiTransparent,
              width: "100%",
              height: "100%",
            }}
          >
            <WfIconSportRyderTypesSpeedster
              sx={{
                color: palette.riderType.attacker.main,
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
            />
          </Avatar>
        ),
        name: "Speedster",
        description: t`When you hit the switch and unleash your speed, few can keep up. Even when running with others who would otherwise be an equal match, you can drop the hammer and open a gap with relative ease. But like any rocket, your range is limited. Save the fireworks for the final heat.`,
      },
      {
        id: FitnessType.Miler,
        stringId: "Miler",
        icon: (
          <WfIconSportRyderTypesMiler
            sx={{ width: "100%", height: "100%", color: palette.riderType.pursuiter.main }}
          />
        ),
        iconFilled: (
          <Avatar
            sx={{
              bgcolor: palette.riderType.pursuiter.semiTransparent,
              width: "100%",
              height: "100%",
            }}
          >
            <WfIconSportRyderTypesMiler
              sx={{
                color: palette.riderType.pursuiter.main,
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
            />
          </Avatar>
        ),
        name: "Miler",
        description: t`If you have five minutes to give it everything, no one else has a chance. You can put your head down and do serious damage on the track.`,
      },
      {
        id: FitnessType.RunningClimber,
        stringId: "RunningClimber",
        icon: (
          <WfIconSportRyderTypesClimber
            sx={{ width: "100%", height: "100%", color: palette.riderType.climber.main }}
          />
        ),
        iconFilled: (
          <Avatar
            sx={{
              bgcolor: palette.riderType.climber.semiTransparent,
              width: "100%",
              height: "100%",
            }}
          >
            <WfIconSportRyderTypesClimber
              sx={{
                color: palette.riderType.climber.main,
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
            />
          </Avatar>
        ),
        name: "Climber",
        description: t`When the trail gets vertical, that's when you get down to business. You seem to defy gravity, dancing up impossibly-steep climbs that leave everyone else begging for supplemental oxygen.`,
      },
      {
        id: FitnessType.TempoAce,
        stringId: "TempoAce",
        icon: (
          <WfIconSportRyderTypesTimetrialist
            sx={{ width: "100%", height: "100%", color: palette.riderType.timeTrialist.main }}
          />
        ),
        iconFilled: (
          <Avatar
            sx={{
              bgcolor: palette.riderType.timeTrialist.semiTransparent,
              width: "100%",
              height: "100%",
            }}
          >
            <WfIconSportRyderTypesTimetrialist
              sx={{
                color: palette.riderType.timeTrialist.main,
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
            />
          </Avatar>
        ),
        name: "Tempo Ace",
        description: t`If it's around an hour, you've got the power. A master of the middle ground. You don't wait for the race to break. You apply pressure early, manage the burn, and know your threshold is higher than most people's willpower.`,
      },
      {
        id: FitnessType.AllRounder,
        stringId: "AllRounder",
        icon: (
          <WfIconSportRyderTypesRouleur
            sx={{ width: "100%", height: "100%", color: palette.riderType.rouleur.main }}
          />
        ),
        iconFilled: (
          <Avatar
            sx={{
              bgcolor: palette.riderType.rouleur.semiTransparent,
              width: "100%",
              height: "100%",
            }}
          >
            <WfIconSportRyderTypesRouleur
              sx={{
                color: palette.riderType.rouleur.main,
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
            />
          </Avatar>
        ),
        name: "All-Rounder",
        description: t`Is there anything you can't do? You are a master of all trades, a Swiss Army Knife, a polyglot of pacing, a...you get the picture. The very definition of versatility, you are able to deliver across a wide range of efforts and terrain. It takes a very specialized runner to best you, whether in a sprint, on a punchy climb, or a full-on marathon. When everyone else falters, you are left standing, usually on top of the podium.`,
      },
      {
        id: FitnessType.Marathoner,
        stringId: "Marathoner",
        icon: (
          <WfIconSportRyderTypesMarathoner
            sx={{ width: "100%", height: "100%", color: palette.riderType.marathoner.main }}
          />
        ),
        iconFilled: (
          <Avatar
            sx={{
              bgcolor: palette.riderType.marathoner.semiTransparent,
              width: "100%",
              height: "100%",
            }}
          >
            <WfIconSportRyderTypesMarathoner
              sx={{
                color: palette.riderType.marathoner.main,
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
            />
          </Avatar>
        ),
        name: "Marathoner",
        description: t`Lord of the long run. Efficiency is your superpower. You don't chase the early surge and were built to sustain the pain. Every stride a quiet flex, you lock in and let time do the damage. When others fade, you just keep charging, turning consistency into the inevitable.`,
      },
    ],
    [
      palette.riderType.attacker.main,
      palette.riderType.attacker.semiTransparent,
      palette.riderType.pursuiter.main,
      palette.riderType.pursuiter.semiTransparent,
      palette.riderType.climber.main,
      palette.riderType.climber.semiTransparent,
      palette.riderType.timeTrialist.main,
      palette.riderType.timeTrialist.semiTransparent,
      palette.riderType.rouleur.main,
      palette.riderType.rouleur.semiTransparent,
      palette.riderType.marathoner.main,
      palette.riderType.marathoner.semiTransparent,
      iconSize,
    ]
  );

  const runnerTypeStrengths: Partial<
    Record<FitnessStrengths, { name: string; summary: string; description: string }>
  > = {
    [FitnessStrengths.RepeatedEfforts]: {
      name: t`Repeated Efforts`,
      summary: t`Repeated efforts`,
      description: t`You have a high Anaerobic Capacity, meaning you can surge hard, recover fast, and surge again. This makes you excellent at workouts with short recovery—like 400m repeats or fartlek runs—and gives you the ability to respond to attacks or make multiple moves during races without blowing up.`,
    },
    [FitnessStrengths.VO2Efforts]: {
      name: t`VO2 Efforts`,
      summary: t`VO2 efforts`,
      description: t`You excel at hard, sustained efforts in the 3-6 minute range—think 1500m or mile races. Relative to your other 3DP metrics, your Maximum Aerobic Pace is above average, meaning you can push a much faster pace when racing shorter distances. This strength gives you a strong kick and makes you dangerous in 5Ks and track races.`,
    },
    [FitnessStrengths.SustainedEfforts]: {
      name: t`Sustained Efforts`,
      summary: t`Sustained efforts`,
      description: t`You have exceptional endurance at threshold. This makes you strong in 10Ks up through the marathon. You can settle into a fast rhythm and hold it, grinding down competitors over longer sustained efforts where aerobic efficiency matters most.`,
    },
    [FitnessStrengths.PrettyMuchEverything]: {
      name: t`Pretty Much Everything`,
      summary: t`You're strong across the board.`,
      description: t`You're good at everything, so you need to train everything. Workouts that feature a variety of efforts in a single session, like Blender, There is No Try and Local Hero on SYSTM are just what you need to maintain your legendary status.`,
    },
  };

  const runnerTypeWeakenesses: Partial<
    Record<FitnessWeakness, { name: string; summary: string; description: string }>
  > = {
    [FitnessWeakness.RepeatedEfforts]: {
      name: t`Repeated Efforts`,
      summary: t`Repeated Efforts`,
      description: t`Your Anaerobic Capacity is low relative to your other abilities, meaning you struggle to recover between hard surges or repeated attacks. Races with variable pacing—like cross country or hilly courses—are tough for you. Focus on short interval workouts (200m-400m repeats with brief recovery) to train your body to clear lactate faster and respond to multiple surges.`,
    },
    [FitnessWeakness.VO2]: {
      name: t`VO2`,
      summary: t`VO2 efforts`,
      description: t`Your Maximum Aerobic Pace—the fastest pace you can sustain for 5 minutes—is limiting your overall potential. This is your aerobic "ceiling," and it caps how much your Threshold Pace can improve. To raise it, focus on hard 3-5 minute intervals well above your Threshold Pace. Improving this will unlock faster performances across all distances.`,
    },
    [FitnessWeakness.SustainedEfforts]: {
      name: t`Sustained Efforts`,
      summary: t`Sustained efforts`,
      description: t`Your running economy and threshold pace are low compared to your other abilities. You struggle to hold a strong pace for extended periods—like in 10Ks or half marathons—because your body isn't efficiently using oxygen at threshold intensity. Focus on tempo runs (20-40 minutes at threshold pace) and long steady runs to build your aerobic base and improve efficiency at race pace.`,
    },
  };

  const getRunnerTypeStrengthById = (id?: FitnessStrengths) => {
    if (!id) {
      return;
    }
    return runnerTypeStrengths[id];
  };

  const getRunnerTypeWeaknessById = (id?: FitnessWeakness) => {
    if (!id) {
      return;
    }
    return runnerTypeWeakenesses[id];
  };

  const getRunnerTypeById = (id?: FitnessType) => {
    if (!id) {
      return;
    }
    return runnerTypes.find((runnerType) => runnerType.id === id);
  };

  return {
    getRunnerTypeStrengthById,
    getRunnerTypeWeaknessById,
    getRunnerTypeById,
  };
};

export default useLocalizedRunningFitnessData;
